import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Badge } from "react-bootstrap";
import { FaBell, FaTimes, FaTrash } from "react-icons/fa";
import io from "socket.io-client";
import axios from "axios";

const NotificationBell = ({ userData }) => {
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [shakeBell, setShakeBell] = useState(false);
  const [socket, setSocket] = useState(null);

  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const audioRef = useRef(null);

  //==========================
  // 1. INITIAL FETCH (OLD NOTIFICATIONS)
  //==========================
  const fetchOld = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_COLLEGE_ERP_API}notification`,
        { 
          params: { branch: userData.branch, userId: userData.userId },
          headers: { Authorization: `Bearer ${userData.token}` } 
        }
      );
      setNotifications(res.data || []);
    } catch (err) {
      console.error("Fetch old notifications failed:", err);
    }
  };

  useEffect(() => {
    if (!userData?.branch) return;
    fetchOld();
  }, [userData]);

  //==========================
  // 2. SOCKET CONNECTION
  //==========================
  useEffect(() => {
    if (!userData?.branch) return;

    const newSocket = io(process.env.REACT_APP_COLLEGE_ERP_API, {
      transports: ["websocket"],
      query: { branch: userData.branch },
    });

    newSocket.emit("joinBranch", {
      branch: userData.branch,
      role: userData.rights,
      senderId: userData.userId,
    });

    setSocket(newSocket);
    return () => newSocket.disconnect();
  }, [userData]);

  //==========================
  // 3. LISTEN FOR LIVE ALERTS
  //==========================
  useEffect(() => {
    if (!socket) return;

    const handleAlert = (msg) => {
      const safeMsg = typeof msg === "object" && msg !== null ? msg : { message: msg };

      const newNote = {
        title: safeMsg.title || "New Notification",
        message: safeMsg.message || "You have a new update",
        ...safeMsg,
        id: Date.now(),
        read: false,
      };

      setNotifications((prev) => [newNote, ...prev]);
      setShakeBell(true);
      setTimeout(() => setShakeBell(false), 600);
      setShowPanel(true);

      try {
        audioRef.current?.play();
      } catch (err) {
        console.warn("Audio play failed:", err);
      }
    };

    socket.on("purchaseAlert", handleAlert);
    socket.on("generalAlert", handleAlert);

    return () => {
      socket.off("purchaseAlert", handleAlert);
      socket.off("generalAlert", handleAlert);
    };
  }, [socket]);

  //==========================
  // 4. DELETE A SINGLE NOTIFICATION
  //==========================
  const deleteOne = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_COLLEGE_ERP_API}notification/${id}`,
        { 
          params: { branch: userData.branch, userId: userData.userId },
          headers: { Authorization: `Bearer ${userData.token}` } 
        }
      );
      setNotifications((prev) => prev.filter((n) => n._id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleSwipeDelete = (note) => {
    const id = note._id;
    setNotifications((prev) => prev.filter((n) => (n._id || n.tempId) !== id));
    if (id) deleteOne(id);
  };

  //==========================
  // 5. DELETE ALL
  //==========================
  const clearAll = async () => {
    const deletedNotes = notifications;
    setNotifications([]);
    await new Promise((res) => setTimeout(res, 300));

    try {
      await Promise.all(
        deletedNotes.map((n) =>
          axios.delete(
            `${process.env.REACT_APP_COLLEGE_ERP_API}notification/${n._id}`,
            {
              params: { branch: userData.branch, userId: userData.userId },
              headers: { Authorization: `Bearer ${userData.token}` },
            }
          )
        )
      );
    } catch (err) {
      console.error("Clear all failed:", err);
    }
  };

  //==========================
  // 6. OUTSIDE CLICK CLOSE
  //==========================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !bellRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!["Admin", "Accounts", "Chairman", "AdminExecutive"].includes(userData?.rights)) return null;

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div style={{ position: "fixed", top: "1rem", right: "1.2rem", zIndex: 4000 }}>
      {/* BELL BUTTON */}
      <Button
        ref={bellRef}
        onClick={() => setShowPanel((prev) => !prev)}
        style={{
          position: "relative",
          padding: 0,
          border: "none",
          background: "transparent",
          fontSize: "1.8rem",
          transform: shakeBell ? "rotate(-15deg)" : "rotate(0deg)",
          transition: "0.15s",
        }}
      >
        <FaBell color="#f0ad4e" />
        {unreadCount > 0 && (
          <Badge
            pill
            bg="danger"
            style={{ position: "absolute", top: "-4px", right: "-4px", fontSize: "0.65rem" }}
          >
            {unreadCount}
          </Badge>
        )}
      </Button>

      {/* PANEL */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            style={{
              position: "absolute",
              top: "2.6rem",
              right: 0,
              width: "340px",
              background: "white",
              borderRadius: "0.6rem",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
              padding: "0.6rem",
              maxHeight: "360px",
              overflowY: "auto",
            }}
          >
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong style={{ fontSize: "1rem" }}>Notifications</strong>
              {notifications.length > 0 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={clearAll}
                  style={{ padding: "2px 6px", fontSize: "0.75rem" }}
                >
                  <FaTrash size={12} /> Clear
                </Button>
              )}
            </div>

            {/* BODY */}
            {notifications.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                style={{ textAlign: "center", padding: "1rem", color: "#777" }}
              >
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 1.3 }}
                  style={{ fontSize: "3.5rem" }}
                >
                  🐣
                </motion.div>
                <div style={{ marginTop: "0.6rem", fontSize: "0.9rem" }}>No Notifications</div>
              </motion.div>
            ) : (
              notifications.map((note) => (
                <motion.div
                  key={note._id || note.tempId}
                  layout
                  initial={{ x: 40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 120, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  style={{
                    background: note.read ? "#fafafa" : "#e9f4ff",
                    borderRadius: "0.4rem",
                    padding: "0.5rem 1.8rem 0.5rem 0.5rem",
                    marginBottom: "0.4rem",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  onClick={() => (window.location.href = "/notifications")}
                >
                  {/* DELETE BUTTON */}
                  <FaTimes
                    size={14}
                    color="#888"
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "6px",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSwipeDelete(note);
                    }}
                  />

                  {/* Dynamic Title */}
                  <div style={{ fontSize: "0.9rem", fontWeight: "600", color: "#333" }}>
                    {note?.title || "Update"}
                  </div>

                  {/* Dynamic Message Text */}
                  <div style={{ fontSize: "0.85rem", color: "#555", margin: "2px 0" }}>
                    {note?.message || ""}
                  </div>

                  {/* Conditional Metadata Sub-Rendering based on "payload" contents */}
                  {note.payload && Object.keys(note.payload).length > 0 && (
                    <div 
                      style={{ 
                        fontSize: "0.78rem", 
                        background: "rgba(0, 0, 0, 0.04)", 
                        padding: "0.5rem", 
                        borderRadius: "0.4rem", 
                        marginTop: "0.5rem",
                        borderLeft: "3px solid #ced4da",
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: "4px 8px"
                      }}
                    >
                      {Object.entries(note.payload).map(([key, value]) => {
                        if (["Branch", "Timestamp"].includes(key)) return null;

                        const formattedKey = key
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())
                          .trim();

                     // 3. Smart Value Formatting (Handles raw values, Mongo $date objects, & raw ISO date strings)
let formattedValue = value;

// A. Handle strings that match an ISO Date timestamp pattern
if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
  formattedValue = new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
// B. Handle MongoDB wrapped date objects ({ $date: "..." })
else if (value && typeof value === "object") {
  if (value.$date) {
    formattedValue = new Date(value.$date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } else {
    // Fallback for generic nested objects
    formattedValue = JSON.stringify(value);
  }
}

                        return (
                          <React.Fragment key={key}>
                            <span style={{ fontWeight: "600", color: "#555" }}>
                              {formattedKey}:
                            </span>
                            <span style={{ color: "#333", wordBreak: "break-word" }}>
                              {String(formattedValue)}
                            </span>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}

                  {/* Timestamp footer */}
                  <div style={{ fontSize: "0.7rem", color: "#999", marginTop: "4px" }}>
                    {note?.Timestamp ? new Date(note.Timestamp).toLocaleString() : "-"}
                  </div>
                </motion.div>
              ))
            )}

            {notifications.length > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => (window.location.href = "/notifications")}
                style={{ width: "100%", marginTop: "6px" }}
              >
                View All
              </Button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;